import _ from 'lodash';
import { PredefinedPayloadExtractor } from '../../../redux/list/list-payload-extractor';
import { appendNewItem } from '../../../redux/list/list-update.reducer';
import {
  createDefaultListState,
  createListReducer,
  ListState,
} from '../../../redux/list/list.reducer';
import { CallApiSuccessResponse } from '../../../redux/middlewares/call-api/call-api';
import { Action, Reducer } from '../../../redux/redux.types';
import { FlowVersion } from '../../../types/flow.types';
import { MaybeUndef } from '../../../types/system';
import {
  ActivateFlowVersionAdditionalData,
  FLOW_VERSION_ACTION_NAMES,
  SaveFlowVersionAdditionalData,
} from './flow-versions-list.actions';
import {
  DeactivateFlowActionAdditionalData,
  FLOWS_LIST_ACTION_NAMES,
} from './flows-list.actions';

export type FlowVersionsListState = ListState<FlowVersion>;

const defaultState: FlowVersionsListState =
  createDefaultListState<FlowVersion>();

const listReducer: Reducer<FlowVersionsListState> =
  createListReducer<FlowVersion>({
    actionNames: [
      FLOW_VERSION_ACTION_NAMES.GET_START,
      FLOW_VERSION_ACTION_NAMES.GET_SUCCESS,
      FLOW_VERSION_ACTION_NAMES.GET_ERROR,
    ],
    payloadExtractorOptions: PredefinedPayloadExtractor.CallApi,
    key: 'revision',
  });

const setSingleActiveVersion = (
  state: FlowVersionsListState,
  activeVersionRevision: number,
): FlowVersionsListState => {
  return {
    ...state,
    itemsMap: _.mapValues(state.itemsMap, (version: FlowVersion) => {
      return {
        ...version,
        active: version.revision === activeVersionRevision,
      };
    }),
  };
};

const NON_EXISTING_REVISION_NUMBER: number = -1;

export const flowVersionsList = (
  state: FlowVersionsListState = defaultState,
  action: Action,
  parentFlowId: MaybeUndef<string>,
): FlowVersionsListState => {
  const listState: FlowVersionsListState = listReducer(state, action);

  switch (action.type) {
    case FLOW_VERSION_ACTION_NAMES.SAVE_SUCCESS: {
      const { responsePayload }: CallApiSuccessResponse<FlowVersion> =
        action.payload;
      const appendedState = appendNewItem(
        listState,
        responsePayload,
        'revision',
      );
      const { flowId }: SaveFlowVersionAdditionalData =
        action.payload.additionalData;

      if (flowId !== parentFlowId) {
        return listState;
      }

      return responsePayload.active
        ? setSingleActiveVersion(appendedState, responsePayload.revision)
        : appendedState;
    }

    case FLOW_VERSION_ACTION_NAMES.ACTIVATE_VERSION_SUCCESS: {
      const { flowId, version }: ActivateFlowVersionAdditionalData =
        action.payload.additionalData;

      if (flowId !== parentFlowId) {
        return listState;
      }

      return setSingleActiveVersion(listState, version.revision);
    }

    case FLOWS_LIST_ACTION_NAMES.DEACTIVATE_FLOW_SUCCESS: {
      const { flowId }: DeactivateFlowActionAdditionalData =
        action.payload.additionalData;

      if (flowId !== parentFlowId) {
        return listState;
      }

      return setSingleActiveVersion(listState, NON_EXISTING_REVISION_NUMBER);
    }

    default:
      return listState;
  }
};
